import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { FileDown, CheckCircle, XCircle, AlertCircle, MinusCircle } from 'lucide-react';
import { toast } from 'sonner';
import StatusBadge from '../components/StatusBadge';

export default function ComplianceManagementPage() {
  const [controls, setControls] = useState([]);
  const [filteredControls, setFilteredControls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [frameworkFilter, setFrameworkFilter] = useState('all');
  const [statusTab, setStatusTab] = useState('all');
  const navigate = useNavigate();

  useEffect(() => {
    fetchControls();
  }, [frameworkFilter]);

  useEffect(() => {
    filterByStatus();
  }, [statusTab, controls]);

  const fetchControls = async () => {
    setLoading(true);
    try {
      const url = frameworkFilter === 'all' 
        ? `${API}/controls` 
        : `${API}/controls?framework=${frameworkFilter}`;
      const response = await axios.get(url);
      setControls(response.data);
      setFilteredControls(response.data);
    } catch (error) {
      toast.error('Failed to fetch controls');
    } finally {
      setLoading(false);
    }
  };

  const filterByStatus = () => {
    let filtered = controls;
    
    if (statusTab === 'compliant') {
      filtered = controls.filter(c => c.status === 'Yes');
    } else if (statusTab === 'non-compliant') {
      filtered = controls.filter(c => c.status === 'No');
    } else if (statusTab === 'partially') {
      filtered = controls.filter(c => c.status === 'Partially');
    } else if (statusTab === 'pending') {
      filtered = controls.filter(c => !c.status || c.status === null);
    }
    
    setFilteredControls(filtered);
  };

  const stats = {
    total: controls.length,
    compliant: controls.filter(c => c.status === 'Yes').length,
    nonCompliant: controls.filter(c => c.status === 'No').length,
    partially: controls.filter(c => c.status === 'Partially').length,
    pending: controls.filter(c => !c.status || c.status === null).length,
  };

  const complianceRate = stats.total > 0 
    ? Math.round((stats.compliant / stats.total) * 100) 
    : 0;

  const handleExport = () => {
    const csvContent = [
      ['Framework', 'Control ID', 'Category', 'Title', 'Requirement', 'Status', 'Evidence', 'Comments'],
      ...filteredControls.map(c => [
        c.framework === 'ISO27001' ? 'ISO 27001' : 'SOC 2',
        c.control_id,
        c.category,
        c.title,
        c.requirement,
        c.status || 'Pending',
        c.evidence || '',
        c.comments || ''
      ])
    ].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `compliance-controls-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success('Export completed');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-lg text-slate-600">Loading controls...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="compliance-management-page">
      {/* Header with Stats */}
      <div>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-heading font-semibold tracking-tight text-slate-900">
              Compliance Management System
            </h1>
            <p className="text-slate-600 mt-2">View and manage compliance status for all controls</p>
          </div>
          
          <select
            data-testid="framework-filter"
            value={frameworkFilter}
            onChange={(e) => setFrameworkFilter(e.target.value)}
            className="rounded-sm border border-input bg-background px-4 py-2 text-sm font-medium w-full sm:w-auto"
          >
            <option value="all">All Frameworks</option>
            <option value="ISO27001">ISO 27001</option>
            <option value="SOC2">SOC 2</option>
          </select>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="stat-card bg-white">
            <div className="text-center">
              <div className="text-3xl font-heading font-bold text-slate-900">{complianceRate}%</div>
              <div className="text-sm text-slate-600 mt-1">Compliance Rate</div>
              <div className="text-xs text-slate-700 mt-1">{stats.compliant} of {stats.total} controls</div>
            </div>
          </div>

          <div className="stat-card bg-white">
            <div className="text-center">
              <div className="text-3xl font-heading font-bold text-red-600">{stats.nonCompliant}</div>
              <div className="text-sm text-slate-600 mt-1">Non-Compliant</div>
              <div className="text-xs text-slate-700 mt-1">Immediate attention</div>
            </div>
          </div>

          <div className="stat-card bg-white">
            <div className="text-center">
              <div className="text-3xl font-heading font-bold text-green-600">{stats.compliant}</div>
              <div className="text-sm text-slate-600 mt-1">Compliant</div>
              <div className="text-xs text-slate-700 mt-1">Fully compliant</div>
            </div>
          </div>

          <div className="stat-card bg-white">
            <div className="text-center">
              <div className="text-3xl font-heading font-bold text-slate-600">{stats.partially}</div>
              <div className="text-sm text-slate-600 mt-1">Partially</div>
              <div className="text-xs text-slate-700 mt-1">Needs improvement</div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs and Export */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex flex-wrap gap-2" data-testid="status-tabs">
          <button
            onClick={() => setStatusTab('all')}
            className={`px-4 py-2 rounded-sm text-sm font-medium transition-colors ${
              statusTab === 'all'
                ? 'bg-slate-900 text-white'
                : 'bg-white border border-border text-slate-700 hover:bg-slate-50'
            }`}
            data-testid="tab-all"
          >
            All Controls ({stats.total})
          </button>
          <button
            onClick={() => setStatusTab('non-compliant')}
            className={`px-4 py-2 rounded-sm text-sm font-medium transition-colors ${
              statusTab === 'non-compliant'
                ? 'bg-slate-600 text-white'
                : 'bg-white border border-border text-slate-700 hover:bg-slate-50'
            }`}
            data-testid="tab-non-compliant"
          >
            Non-Compliant ({stats.nonCompliant})
          </button>
          <button
            onClick={() => setStatusTab('compliant')}
            className={`px-4 py-2 rounded-sm text-sm font-medium transition-colors ${
              statusTab === 'compliant'
                ? 'bg-slate-600 text-white'
                : 'bg-white border border-border text-slate-700 hover:bg-slate-50'
            }`}
            data-testid="tab-compliant"
          >
            Compliant ({stats.compliant})
          </button>
          <button
            onClick={() => setStatusTab('partially')}
            className={`px-4 py-2 rounded-sm text-sm font-medium transition-colors ${
              statusTab === 'partially'
                ? 'bg-slate-600 text-white'
                : 'bg-white border border-border text-slate-700 hover:bg-slate-50'
            }`}
            data-testid="tab-partially"
          >
            Partially ({stats.partially})
          </button>
        </div>

        <Button
          onClick={handleExport}
          variant="outline"
          className="rounded-sm w-full sm:w-auto"
          data-testid="export-button"
        >
          <FileDown className="w-4 h-4 mr-2" />
          Export
        </Button>
      </div>

      {/* Controls Table - Desktop */}
      <div className="hidden md:block border border-border rounded-sm bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-border">
              <tr>
                <th className="px-3 py-3 text-left text-xs font-medium text-slate-700 uppercase w-24">Framework</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-slate-700 uppercase w-28">Control ID</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-slate-700 uppercase w-48">Category</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-slate-700 uppercase">Name & Description</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-slate-700 uppercase w-32">Status</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-slate-700 uppercase w-32">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filteredControls.map((control, index) => (
                <tr
                  key={control.id}
                  data-testid={`control-row-${index}`}
                  className="hover:bg-slate-50 transition-colors"
                >
                  <td className="px-3 py-4">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-sm text-xs font-medium ${
                      control.framework === 'ISO27001' 
                        ? 'bg-slate-100 text-slate-800' 
                        : 'bg-slate-100 text-slate-800'
                    }`}>
                      {control.framework === 'ISO27001' ? 'ISO' : 'SOC2'}
                    </span>
                  </td>
                  <td className="px-3 py-4">
                    <span className="text-sm font-mono font-medium text-slate-900">
                      {control.control_id}
                    </span>
                  </td>
                  <td className="px-3 py-4">
                    <span className="text-xs text-slate-600">{control.category}</span>
                  </td>
                  <td className="px-3 py-4">
                    <div>
                      <div className="text-sm font-medium text-slate-900 mb-1">{control.title}</div>
                      <div className="text-xs text-slate-600 line-clamp-2">{control.requirement}</div>
                    </div>
                  </td>
                  <td className="px-3 py-4">
                    <StatusBadge status={control.status} />
                  </td>
                  <td className="px-3 py-4">
                    <Button
                      data-testid={`view-details-${index}`}
                      onClick={() => navigate(`/controls/${control.id}`)}
                      variant="outline"
                      size="sm"
                      className="rounded-sm text-xs"
                    >
                      View Details
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Controls Cards - Mobile */}
      <div className="md:hidden space-y-3">
        {filteredControls.map((control, index) => (
          <div
            key={control.id}
            data-testid={`control-card-${index}`}
            className="border border-border rounded-sm bg-white p-4 space-y-3"
          >
            <div className="flex items-start justify-between gap-2">
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`inline-flex items-center px-2 py-1 rounded-sm text-xs font-medium ${
                  control.framework === 'ISO27001' 
                    ? 'bg-slate-100 text-slate-800' 
                    : 'bg-slate-100 text-slate-800'
                }`}>
                  {control.framework === 'ISO27001' ? 'ISO' : 'SOC2'}
                </span>
                <span className="text-sm font-mono font-medium text-slate-900">{control.control_id}</span>
              </div>
              <StatusBadge status={control.status} />
            </div>
            
            <div>
              <div className="text-xs text-slate-700 mb-1">{control.category}</div>
              <h3 className="text-sm font-medium text-slate-900 mb-1">{control.title}</h3>
              <p className="text-xs text-slate-600 line-clamp-2">{control.requirement}</p>
            </div>
            
            <Button
              data-testid={`view-details-mobile-${index}`}
              onClick={() => navigate(`/controls/${control.id}`)}
              variant="outline"
              size="sm"
              className="w-full rounded-sm"
            >
              View Details
            </Button>
          </div>
        ))}
      </div>

      {filteredControls.length === 0 && (
        <div className="text-center py-12 bg-white border border-border rounded-sm">
          <div className="text-slate-600 mb-2">
            {statusTab === 'compliant' && <CheckCircle className="w-12 h-12 mx-auto" />}
            {statusTab === 'non-compliant' && <XCircle className="w-12 h-12 mx-auto" />}
            {statusTab === 'partially' && <AlertCircle className="w-12 h-12 mx-auto" />}
            {statusTab === 'all' && <MinusCircle className="w-12 h-12 mx-auto" />}
          </div>
          <p className="text-slate-600">No {statusTab !== 'all' ? statusTab : ''} controls found</p>
        </div>
      )}
    </div>
  );
}
